import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, Check, User, Briefcase, Network, Building, KeyRound, AtSign } from 'lucide-react';
import { TeamMember, Project } from './shared/types';
import { api } from '../lib/api';

interface TeamManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    project: Project;
    teamMembers: TeamMember[];
    onTeamUpdate: () => void;
}

export const TeamManagementModal: React.FC<TeamManagementModalProps> = ({
    isOpen,
    onClose,
    project,
    teamMembers,
    onTeamUpdate
}) => {
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'list' | 'hierarchy'>('hierarchy'); // Default to hierarchy
    const [buildings, setBuildings] = useState<any[]>([]);

    useEffect(() => {
        if (project.id) {
            api.getMasterData(String(project.id)).then(data => {
                setBuildings(data.buildings || []);
            }).catch(console.error);
        }
    }, [project.id]);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        reportsTo: '',
        buildingId: '',
        username: '',
        password: '',
    });

    const resetForm = () => {
        setFormData({ name: '', role: '', reportsTo: '', buildingId: '', username: '', password: '' });
        setIsAdding(false);
        setEditingId(null);
        setError(null);
    };

    const handleEdit = (member: TeamMember) => {
        setFormData({
            name: member.name,
            role: member.role,
            reportsTo: member.reportsTo ? String(member.reportsTo) : '',
            buildingId: member.buildingId ? String(member.buildingId) : '',
            username: member.username || '',
            password: '',
        });
        setEditingId(member.id);
        setIsAdding(true);
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to delete this position?')) return;
        try {
            await api.deleteTeamMember(id);
            onTeamUpdate();
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        try {
            const payload: any = {
                name: formData.name,
                role: formData.role,
                project_id: Number(project.id),
                reports_to: formData.reportsTo ? Number(formData.reportsTo) : null,
                building_id: formData.buildingId || null,
                ...(formData.username.trim() ? { username: formData.username.trim() } : {}),
                ...(formData.password.trim() ? { password: formData.password.trim() } : {}),
            };

            if (editingId) {
                await api.updateTeamMember(editingId, payload);
            } else {
                await api.addTeamMember(payload);
            }

            onTeamUpdate();
            resetForm();
        } catch (err: any) {
            setError(err.message);
        }
    };

    if (!isOpen) return null;

    // Function to build hierarchy string
    const getManagerName = (managerId?: number) => {
        if (!managerId) return '-';
        const manager = teamMembers.find(m => m.id === managerId);
        return manager ? manager.name : 'Unknown';
    };

    // --- Hierarchy Logic ---
    const buildHierarchy = () => {
        const tree: any[] = [];
        const map: any = {};

        // Initialize map
        teamMembers.forEach(m => {
            map[m.id] = { ...m, children: [] };
        });

        // Build tree
        teamMembers.forEach(m => {
            if (m.reportsTo && map[m.reportsTo]) {
                map[m.reportsTo].children.push(map[m.id]);
            } else {
                tree.push(map[m.id]);
            }
        });

        return tree;
    };

    const TeamTreeNode = ({ member, level = 0 }: { member: any, level?: number }) => (
        <div className="select-none">
            <div
                className={`group flex items-center justify-between p-2 hover:bg-gray-50 rounded ${level === 0 ? 'bg-gray-50' : ''}`}
                style={{ marginLeft: `${level * 20}px` }}
            >
                <div className="flex items-center gap-2">
                    {level > 0 && <span className="text-gray-300">└─</span>}
                    <div className="flex flex-col">
                        <span className={`font-medium ${level === 0 ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
                            {member.name}
                        </span>
                        <span className="text-xs text-blue-600 font-normal">
                            {member.role}
                        </span>
                        {member.buildingName && (
                            <span className="text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded mt-0.5 w-fit flex items-center gap-1">
                                <Building size={10} /> {member.buildingName}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Buttons shown on hover usually, but keeping visible based on previous design for clarity */}
                    <button onClick={() => handleEdit(member)} className="p-1 text-gray-400 hover:text-blue-600">
                        <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(member.id)} className="p-1 text-gray-400 hover:text-red-600">
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>
            {member.children && member.children.length > 0 && (
                <div className="ml-4 pl-0">
                    {member.children.map((child: any) => (
                        <TeamTreeNode key={child.id} member={child} level={level + 1} />
                    ))}
                </div>
            )}
        </div>
    );


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Team Management</h2>
                        <p className="text-sm text-gray-600">{project.name}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                        {/* View Toggle */}
                        <div className="flex rounded-lg bg-gray-100 p-1">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                            >
                                Flat List
                            </button>
                            <button
                                onClick={() => setViewMode('hierarchy')}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${viewMode === 'hierarchy' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                            >
                                Hierarchy / Org Chart
                            </button>
                        </div>
                        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex">
                    {/* Left: Team List / Tree */}
                    <div className="flex-1 overflow-y-auto p-4 border-r bg-white">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                <User className="w-4 h-4 text-gray-500" />
                                {viewMode === 'hierarchy' ? 'Organization Structure' : `All Positions (${teamMembers.length})`}
                            </h3>
                            <button
                                onClick={() => { resetForm(); setIsAdding(true); }}
                                className="flex items-center gap-2 text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
                            >
                                <Plus size={16} /> Add Position
                            </button>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
                                {error}
                            </div>
                        )}

                        <div className="space-y-1">
                            {viewMode === 'list' ? (
                                teamMembers.map(member => (
                                    <div key={member.id} className="group flex items-center justify-between p-3 bg-white border rounded-lg hover:border-blue-400 hover:shadow-sm transition-all">
                                        <div className="flex-1">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-gray-900">{member.name}</span>
                                                    {member.username && (
                                                        <span className="text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                            <KeyRound size={10} /> {member.username}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-xs text-blue-600 font-normal mt-0.5">{member.role}</span>
                                                {member.buildingName && (
                                                    <span className="text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded mt-1 w-fit flex items-center gap-1">
                                                        <Building size={10} /> {member.buildingName}
                                                    </span>
                                                )}
                                            </div>
                                            {member.reportsTo && (
                                                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                    <Network size={12} />
                                                    Reports to: <span className="font-medium text-gray-700">{getManagerName(member.reportsTo)}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => handleEdit(member)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                                                <Edit2 size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(member.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="pl-2">
                                    {buildHierarchy().map((node: any) => (
                                        <TeamTreeNode key={node.id} member={node} />
                                    ))}
                                    {teamMembers.length === 0 && (
                                        <div className="text-center py-10 text-gray-400 text-sm">
                                            No team members added yet.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Add/Edit Form */}
                    {isAdding && (
                        <div className="w-96 bg-gray-50 p-6 border-l overflow-y-auto shadow-inner">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-bold text-lg text-gray-900">{editingId ? 'Edit Position' : 'New Position'}</h3>
                                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-200 rounded-full transition-colors">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Position / Name</label>
                                    <div className="relative">
                                        <User size={16} className="absolute left-3 top-3 text-gray-400" />
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. Rahul Sharma"
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Role / Designation</label>
                                    <div className="relative">
                                        <Briefcase size={16} className="absolute left-3 top-3 text-gray-400" />
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                                            placeholder="e.g. Senior Engineer"
                                            value={formData.role}
                                            onChange={e => setFormData({ ...formData, role: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Reports To (Manager)</label>
                                    <div className="relative">
                                        <Network size={16} className="absolute left-3 top-3 text-gray-400" />
                                        <select
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow bg-white"
                                            value={formData.reportsTo}
                                            onChange={e => setFormData({ ...formData, reportsTo: e.target.value })}
                                        >
                                            <option value="">Top Level (No Manager)</option>
                                            {teamMembers
                                                .filter(m => m.id !== editingId) // Prevent reporting to self
                                                .map(m => (
                                                    <option key={m.id} value={m.id}>
                                                        {m.name} ({m.role})
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">Select who this person reports to in the hierarchy.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Tower / Building (Optional)</label>
                                    <div className="relative">
                                        <Building size={16} className="absolute left-3 top-3 text-gray-400" />
                                        <select
                                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow bg-white"
                                            value={formData.buildingId}
                                            onChange={e => setFormData({ ...formData, buildingId: e.target.value })}
                                        >
                                            <option value="">All Towers / General Project</option>
                                            {buildings.map(b => (
                                                <option key={b.id} value={b.id}>
                                                    {b.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">If assigned to a specific tower, select it here.</p>
                                </div>

                                {/* App Login Credentials */}
                                <div className="border-t border-gray-200 pt-5">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                        <KeyRound size={12} /> Mobile App Login
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
                                            <div className="relative">
                                                <AtSign size={16} className="absolute left-3 top-3 text-gray-400" />
                                                <input
                                                    type="text"
                                                    placeholder="e.g. rahul.sharma"
                                                    autoCapitalize="none"
                                                    autoCorrect="off"
                                                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                                    value={formData.username}
                                                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                                Password {editingId && <span className="text-gray-400 font-normal">(leave blank to keep existing)</span>}
                                            </label>
                                            <div className="relative">
                                                <KeyRound size={16} className="absolute left-3 top-3 text-gray-400" />
                                                <input
                                                    type="password"
                                                    placeholder={editingId ? '••••••• (unchanged)' : 'Set a password'}
                                                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                                    value={formData.password}
                                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mt-2 text-xs text-gray-400">Engineer uses this to log into the SiteSmart mobile app.</p>
                                </div>

                                <div className="pt-4 flex gap-3">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center justify-center gap-2 shadow-sm transition-colors"
                                    >
                                        <Check size={18} /> {editingId ? 'Update Position' : 'Create Position'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="flex-1 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
